import { z } from 'zod';
import { SubmitAiFeedbackRequestSchema, SubmitAiFeedbackResponseSchema } from '../submit-ai-feedback/submit-ai-feedback.schema';
import { AppChatStartersSchema, ChatWithAppRequestSchema, ListAppChatSessionsResponseSchema, LoadAppChatHistoryResponseSchema, PresignAppChatUploadRequestSchema, PresignAppChatUploadResponseSchema, StartAppChatRequestSchema, StartAppChatResponseSchema } from './app-chat.schema';
/**
 * The APP gate is shared by all seven endpoints and re-evaluated on every call:
 * the app installed + enabled, its registry entry `published`, the manifest
 * declaring the kind, and the kind's visibility roles. A copied deep link
 * therefore fails with 403 rather than resuming someone else's session.
 *
 * The tenant's master `enableAI` gates only `start`, `presign`, `starters` and
 * the turn itself — the routes that run the app or the model. Listing sessions,
 * loading a transcript and rating an answer stay reachable when it is off.
 */ export const startAppChatContract = {
    method: 'POST',
    path: '/api/v2/app-chat/:appId/:kind/start',
    description: "Open or resume the shared chat session for one subject of an app's chat kind",
    requestSchema: StartAppChatRequestSchema,
    responseSchema: StartAppChatResponseSchema,
    statusCodes: {
        success: 201,
        errors: {
            400: 'Invalid request body, or the chat kind defines no initializer',
            401: 'Unauthorized - user not authenticated',
            403: 'AI disabled for the tenant, or the app / chat kind is not visible to this user',
            404: 'App not installed, not published, or the chat kind is not declared'
        }
    }
};
export const chatWithAppContract = {
    method: 'POST',
    path: '/api/v2/app-chat/:appId/:kind',
    description: 'Send one message to an app chat session (streaming response)',
    requestSchema: ChatWithAppRequestSchema,
    responseSchema: z.any().describe('SSE stream following AI SDK UIMessage protocol'),
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid request body',
            401: 'Unauthorized - user not authenticated',
            403: 'AI disabled for the tenant, or the app / chat kind is not visible to this user',
            404: 'App, chat kind, or session not found'
        }
    }
};
/**
 * Mint a presigned POST for one app-chat attachment.
 *
 * The upload never travels through the backend, but the destination is not the
 * client's to choose: the key is built from the caller's verified tenant and
 * user id plus the app whose gate this call just passed, and the POST policy
 * pins the content type and the byte ceiling so the object store rejects a
 * mismatched body on our behalf.
 *
 * Gated exactly like the turn route — an upload only ever exists to feed a chat
 * turn, so a caller who may not chat with this kind must not be able to put
 * files into the bucket through it either.
 */ export const presignAppChatUploadContract = {
    method: 'POST',
    path: '/api/v2/app-chat/:appId/:kind/uploads/presign',
    description: 'Mint a presigned POST for an app-chat attachment',
    requestSchema: PresignAppChatUploadRequestSchema,
    responseSchema: PresignAppChatUploadResponseSchema,
    statusCodes: {
        success: 201,
        errors: {
            400: 'Bad request - unsupported media type or oversize file',
            401: 'Unauthorized - user not authenticated',
            403: 'AI disabled for the tenant, or the app / chat kind is not visible to this user',
            404: 'App not installed, not published, or the chat kind is not declared'
        }
    }
};
/**
 * The caller's OWN sessions of a `per-user-private` chat kind.
 *
 * A `shared-by-subject` kind answers 404, not 403: it has no list surface at
 * all — its sessions are addressed by subject — and refusing by absence keeps
 * this route from reporting which kinds exist in which mode.
 */ export const listAppChatSessionsContract = {
    method: 'GET',
    path: '/api/v2/app-chat/:appId/:kind/sessions',
    description: "List the caller's own sessions of a per-user-private chat kind",
    responseSchema: ListAppChatSessionsResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'The app / chat kind is not visible to this user',
            404: 'App not installed, not published, the chat kind is not declared, or the kind is not per-user-private'
        }
    }
};
/**
 * The caller's suggested empty-state questions for one chat kind, per user and
 * per language.
 *
 * Served from the platform cache, which is refreshed behind the answer while it
 * is still worth showing (24h fresh, 7 days stale). A kind that declares no
 * starters hook, and a generation that failed or timed out, both answer with an
 * empty list — the empty state renders nothing rather than an error.
 */ export const listAppChatStartersContract = {
    method: 'GET',
    path: '/api/v2/app-chat/:appId/:kind/starters',
    description: "Suggested starter questions for an app chat kind's empty state",
    responseSchema: AppChatStartersSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - user not authenticated',
            403: 'AI disabled for the tenant, or the app / chat kind is not visible to this user',
            404: 'App not installed, not published, or the chat kind is not declared'
        }
    }
};
/**
 * A thumbs verdict on one app-chat answer.
 *
 * The SAME body as the Bliksy feedback route — one verdict protocol, restated
 * nowhere. The app and the kind ride the path, as they do on
 * every other app-chat route, because they are what the gate is evaluated
 * against; the session is then resolved through the same owner predicate the
 * transcript GET uses, so a vote can only reach an answer the caller may read.
 */ export const submitAppChatFeedbackContract = {
    method: 'POST',
    path: '/api/v2/app-chat/:appId/:kind/feedback',
    description: 'Submit, change or retract a thumbs verdict on an app-chat answer',
    requestSchema: SubmitAiFeedbackRequestSchema,
    responseSchema: SubmitAiFeedbackResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Bad Request - invalid feedback payload',
            401: 'Unauthorized - user not authenticated',
            403: 'The app / chat kind is not visible to this user',
            404: 'App, chat kind, or session not found, or the message is not an assistant message of it'
        }
    }
};
export const loadAppChatHistoryContract = {
    method: 'GET',
    path: '/api/v2/app-chat/:appId/:kind/sessions/:sessionId/history',
    description: 'Load the stored transcript of an app chat session',
    responseSchema: LoadAppChatHistoryResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid session id',
            401: 'Unauthorized - user not authenticated',
            403: 'The app / chat kind is not visible to this user',
            404: 'App, chat kind, or session not found'
        }
    }
};

//# sourceMappingURL=app-chat.contract.js.map